import { useState } from 'react'
import { useQuestionnaireVersions, useQuestionnaireManagement } from '../hooks/useQuestionnaire'
import { useAdminAuth } from '../hooks/useAdminAuth'
import { Check, Eye, GitCompare, Trash2, RefreshCw, AlertCircle } from 'lucide-react'

function QuestionnaireVersionHistory({ onCompareVersions, onVersionActivated }) {
  const { versions, loading, error, refetch } = useQuestionnaireVersions()
  const { activateVersion, deleteVersion, loading: actionLoading } = useQuestionnaireManagement()
  const { isAdmin, isEditor, isReader } = useAdminAuth()
  const [actionError, setActionError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  const handleActivate = async (version) => {
    if (!confirm(`Êtes-vous sûr de vouloir activer la version ${version} ? Cela désactivera la version actuelle.`)) {
      return
    }

    try {
      setActionError(null)
      setSuccessMessage(null)
      await activateVersion(version)
      setSuccessMessage(`Version ${version} activée avec succès`)
      refetch()
      
      // Notify parent to refetch active questionnaire
      if (onVersionActivated) {
        onVersionActivated()
      }
    } catch (err) {
      setActionError(`Erreur lors de l'activation: ${err.message}`)
    }
  }

  const handleDelete = async (version) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer la version ${version} ? Cette action ne peut pas être annulée.`)) {
      return
    }

    try {
      setActionError(null)
      setSuccessMessage(null)
      await deleteVersion(version)
      setSuccessMessage(`Version ${version} supprimée avec succès`)
      refetch()
    } catch (err) {
      setActionError(`Erreur lors de la suppression: ${err.message}`)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        <span className="ml-2 text-gray-600">Chargement des versions...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <p className="text-red-700">Erreur: {error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{successMessage}</p>
        </div>
      )}
      
      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <p className="text-red-700">{actionError}</p>
          </div>
        </div>
      )}

      {/* Role-based permissions info */}
      {isReader() && !isEditor() && !isAdmin() && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <strong>Mode Lecture seule</strong> - Vous pouvez consulter l'historique mais pas modifier les versions. 
              Contactez un administrateur pour activer ou supprimer des versions.
            </div>
          </div>
        </div>
      )}

      {/* Versions Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Version
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Créée le
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Par
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Thèmes/Questions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Changelog
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 shadow-[-2px_0_4px_rgba(0,0,0,0.05)]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {versions.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500 bg-white">
                  Aucune version trouvée
                </td>
              </tr>
            ) : (
              versions.map((v) => (
                <tr key={v.version} className={v.isActive ? 'bg-blue-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">
                        v{v.version}
                      </span>
                      {v.isDeleted && (
                        <span className="ml-2 px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                          Supprimée
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {v.isActive ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Check className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(v.metadata.createdAt).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {v.metadata.createdBy}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {v.summary.totalThemes} / {v.summary.totalQuestions}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 min-w-[200px] max-w-md truncate">
                    {v.metadata.changeLog || 'No changelog provided'}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 shadow-[-2px_0_4px_rgba(0,0,0,0.05)] ${v.isActive ? 'bg-blue-50' : 'bg-white'}`}>
                    <div className="flex items-center justify-end space-x-2">
                      {/* Compare Button */}
                      {onCompareVersions && !v.isDeleted && (
                        <button
                          onClick={() => onCompareVersions(v.version)}
                          className="inline-flex items-center p-1 text-purple-600 hover:text-purple-900 hover:bg-purple-100 rounded"
                          title="Comparer avec active"
                        >
                          <GitCompare className="w-4 h-4" />
                        </button>
                      )}
                      
                      {/* Activate Button (editor/admin, not for active or deleted) */}
                      {(isEditor() || isAdmin()) && !v.isActive && !v.isDeleted && (
                        <button
                          onClick={() => handleActivate(v.version)}
                          disabled={actionLoading}
                          className="inline-flex items-center p-1 text-green-600 hover:text-green-900 hover:bg-green-100 rounded disabled:opacity-50"
                          title="Activer cette version"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      
                      {/* Delete Button (admin only, not for active) */}
                      {isAdmin() && !v.isActive && !v.isDeleted && (
                        <button
                          onClick={() => handleDelete(v.version)}
                          disabled={actionLoading}
                          className="inline-flex items-center p-1 text-red-600 hover:text-red-900 hover:bg-red-100 rounded disabled:opacity-50"
                          title="Supprimer cette version"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <button
          onClick={refetch}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>
    </div>
  )
}

export default QuestionnaireVersionHistory

